import { Helmet } from "react-helmet";
import { Container } from "react-bootstrap";
import "../Modules/protect/protect-page.css";
import Footer from "../Modules/Footer";
import Hero from "../Modules/home/Hero";
import NavigationBar from "../Modules/NavigationBar";
import PoweredByDopple from "../Modules/PoweredByDopple";

const icon = (name: string) => `/brand/icons/${name}.svg`;

const features = [
	{ icon: "hearing-protection", title: "Hearing Protection", bullets: ["Continuous noise protection of 27dB", "Impulse Protection level up to 38dB", "Always-on protection, even when powered down"] },
	{ icon: "active-awareness", title: "Active Awareness", bullets: ["Through Smart Dynamic Damping™, hear the sounds that matter while staying protected"] },
	{ icon: "custom-fit", title: "All-day Comfort", bullets: ["Custom-fit to the unique shape of your ears using biocompatible resin, providing exceptional comfort and a secure fit throughout the day"] },
	{ icon: "hands-free-calling", title: "Hands-free", bullets: ["Phone calls in noisy environments using Bone Conduction technology and advanced Noise Suppression algorithms", "Bluetooth® 6.1 provides dependable, hands-free performance", "Active voice assistant"] },
	{ icon: "high-quality-music", title: "HQ Music", bullets: ["Powerful bass and crisp highs from the Dynamic Driver, safely limited to 80dB", "Equalize your music to your liking"] },
	{ icon: "mobile-app", title: "Mobile App", bullets: ["Fine-tune your Active Awareness", "Software updates", "Customizable button configuration"] },
	{ icon: "dutch-made-quality", title: "Dutch-made", bullets: ["Designed and produced in the Netherlands", "Robust with an IP57-rated Dust & Water-protection"] },
	{ icon: "all-day-power", title: "All-day Power", bullets: ["8h+ Music with Smart Dynamic Damping™", "5h+ Call-time", "Extend by 16h+ with Charging Case", "USB-C & Wireless charging"] },
];

const modes = [
	{ name: "Fixed", sdd: false, tag: "Mode", text: "Fixed, continuous hearing protection for the loudest environments.", shot: "fixed", alt: "The Fixed mode in the myProtect app" },
	{ name: "Dynamic", sdd: true, tag: "Default mode", text: "Dynamic, Intelligent adaptive hearing protection for everyday use.", shot: "dynamic", alt: "The Dynamic mode in the myProtect app", primary: true },
	{ name: "Communication", sdd: true, tag: "Mode", text: "Communication, Optimized speech clarity for conversations.", shot: "communication", alt: "The Communication mode in the myProtect app" },
];

const photos = [
	{ src: "/images/home/sparks.jpg", alt: "Sparks flying from an angle grinder" },
	{ src: "/images/home/welder.jpg", alt: "A welder at work with sparks flying" },
	{ src: "/images/home/grinder.jpg", alt: "A worker cutting stone with an angle grinder" },
];

const appScreens = [
	{ src: "damping", alt: "Protect through dynamic damping" },
	{ src: "modes", alt: "Switch between noise protection modes" },
	{ src: "call", alt: "Call in any environment" },
	{ src: "gestures", alt: "Customise gesture controls" },
	{ src: "equalizer", alt: "Personalise equalizer settings" },
	{ src: "updates", alt: "Update your Earbuds" },
];

export default function Protect() {
	return (
		<>
			<Helmet>
				<title>{"EarsOnly | Protect"}</title>
			</Helmet>
			<NavigationBar />
			<Hero logo="/brand/wordmarks/earsonly-protect-black.svg" />

			<section className="eo-rhero eo-rhero--wide">
				<div className="eo-rhero__photos">
					{photos.map((p) => (
						<img key={p.src} src={p.src} alt={p.alt} />
					))}
				</div>
				<Container className="eo-rhero__grid">
					<img className="eo-rhero__product" src="/images/protect/render.png" alt="EarsOnly Protect Earbuds in their open Charging Case" />
					<div className="eo-rhero__text">
						<img className="eo-pintro__wordmark" src="/brand/wordmarks/protect-black.svg" alt="Protect" />
						<h2 className="eo-section__title">Protect your ears All day. Every day.</h2>
						<p className="eo-pintro__lead">
							The EarsOnly Protect is the most intelligent custom-fit hearing protection system powered by Smart Dynamic Damping™, automatically protecting your hearing while keeping you aware, connected and in control.
						</p>

					</div>
				</Container>
			</section>

			<Container className="eo-pfeatures">
				<p className="eo-section__overline">Features</p>
				<h2 className="eo-section__title">What makes Protect different.</h2>
				<div className="eo-features">
					{features.map((f) => (
						<div className="eo-feature" key={f.title}>
							<img className="eo-feature__icon" src={icon(f.icon)} alt="" />
							<h3 className="eo-feature__title">{f.title}</h3>
							<ul className="eo-feature__list">
								{f.bullets.map((b) => (
									<li key={b}>{b}</li>
								))}
							</ul>
						</div>
					))}
				</div>
			</Container>

			<section className="eo-pspec">
				<Container>
					<p className="eo-section__overline">Specifications</p>
					<h2 className="eo-section__title">Rated and certified.</h2>
					<img className="eo-pspec__rating" src="/images/protect/rated-certified.svg" alt="SNR 27 dB, H 31 dB, M 24 dB, L 22 dB. IPIL 38 dB at 165 dB. Hearing protection certified to EN352-2, 7, 9, 10." loading="lazy" />
					<p className="eo-spec__note"><strong>CERTIFIED:</strong> EN352-2, 7, 9, 10 · ANSI S12.42-2010 · IP57 · CE-approved</p>
				</Container>
			</section>

			<section className="eo-pmodes" data-theme="dark">
				<Container>
					<p className="eo-section__overline">Three modes</p>
					<h2 className="eo-section__title">Smart Dynamic Damping™</h2>
					<p className="eo-pmodes__lead">
						Developed by Dopple, Smart Dynamic Damping™ continuously adapts to changing sound environments, automatically limiting harmful environmental noise to a safe maximum of 80 dB while preserving the sounds that matter most. It is active in Dynamic and Communication mode; Fixed mode keeps the protection constant.
					</p>
					<div className="eo-modes">
						{modes.map((m) => (
							<div key={m.name} className={`eo-mode${m.primary ? " eo-mode--primary" : ""}${m.sdd ? " eo-mode--sdd" : ""}`}>
								<span className={`eo-mode__sdd${m.sdd ? " is-on" : ""}`}>{m.sdd ? "Smart Dynamic Damping™" : "No damping, always fixed"}</span>
								<h3 className="eo-mode__name">{m.name}</h3>
								<p className="eo-mode__tag">{m.tag}</p>
								<p className="eo-mode__text">{m.text}</p>
								<img className="eo-mode__shot" src={`/images/protect/mode-${m.shot}.png`} alt={m.alt} loading="lazy" />
							</div>
						))}
					</div>
				</Container>
			</section>

			<section className="eo-pcall">
				<img src="/images/protect/in-call.jpg" alt="A man on a phone call in a noisy work vehicle" loading="lazy" />
				<Container className="eo-pcall__text">
					<p className="eo-section__overline">Hands-free</p>
					<h2 className="eo-section__title">Call in any environment.</h2>
					<p>Phone calls in noisy environments using Bone Conduction technology and advanced Noise Suppression algorithms.</p>
				</Container>
			</section>

			<Container className="eo-papp">
				<p className="eo-section__overline">myProtect</p>
				<h2 className="eo-section__title">Fine-tune your Active Awareness.</h2>
				<div className="eo-papp__grid">
					{appScreens.map((s) => (
						<img key={s.src} src={`/images/protect/app-${s.src}.jpg`} alt={s.alt} loading="lazy" />
					))}
				</div>
			</Container>

			<PoweredByDopple />

			<Container className="eo-pcta">
				<h2 className="eo-section__title">Always Protected.</h2>
				<div className="eo-pintro__actions">
					<a className="eo-button eo-button--accent" href="/contact">Order</a>
					<a className="eo-button eo-button--outline" href="/faq?product=PRO-TW2-ORG">Support</a>
					<a className="eo-dopple-inline" href="https://dopple.nl/" target="_blank" rel="noreferrer">
						<img src="/brand/powered-by-dopple-white.svg" alt="Powered by Dopple" loading="lazy" />
					</a>
				</div>

			</Container>
			<Footer />
		</>
	);
}
